/**
 * @file     clientReweight.ts
 * @purpose  Client-safe re-weighting helper. Mirrors the math from
 *           rescoreBreakdown without pulling the full component-plugin
 *           registry into a client bundle. Used by /daily-hot-deals when
 *           the user toggles the profile lens.
 *
 *           Profile weight tables are duplicated here intentionally — the
 *           server registry remains the source of truth for scoring, this
 *           file is the read-only mirror for browser code.
 *
 *           Adding a profile requires updating both this file and
 *           ./registry.ts; the engine-v2-weight-validation test guards the
 *           registry side; this file's tests guard the mirror.
 */
import type { DealBucket, ScoreBreakdown, ScoreComponent } from '../types';

export type ClientProfileId = 'buyer' | 'investor' | 'first_time_buyer';

// 2026-06-10 (ENGINE_VERSION 2.1.0): bcAssessment retired pre-VOW; weights are
// the previous tables renormalized by (1 - bca weight) per profile. Mirrors
// engine/registry.ts exactly — keep in lock-step.
export const PROFILE_WEIGHTS: Record<ClientProfileId, Record<keyof ScoreBreakdown, number>> = {
  buyer: {
    priceVsComps: 0.2391,
    priceHistory: 0.1956,
    hpiDeviation: 0.1304,
    marketContext: 0.0870,
    carryingCost: 0.0870,
    transitAccess: 0.1087,
    schoolQuality: 0.0870,
    hazardRisk: 0.0326,
    crimeDensity: 0.0326,
  },
  investor: {
    priceVsComps: 0.1684,
    priceHistory: 0.1263,
    hpiDeviation: 0.1684,
    marketContext: 0.1684,
    carryingCost: 0.1684,
    transitAccess: 0.1053,
    schoolQuality: 0.0316,
    hazardRisk: 0.0316,
    crimeDensity: 0.0316,
  },
  first_time_buyer: {
    priceVsComps: 0.1684,
    priceHistory: 0.1684,
    hpiDeviation: 0.0842,
    marketContext: 0.0842,
    carryingCost: 0.2631,
    transitAccess: 0.1053,
    schoolQuality: 0.0632,
    hazardRisk: 0.0316,
    crimeDensity: 0.0316,
  },
};

const DEFAULT_THRESHOLDS = {
  smoking_hot: 85,
  worth_a_look: 70,
  fair_value: 55,
};

export interface ReweightResult {
  score: number;
  bucket: DealBucket;
}

/**
 * Re-weight a stored ScoreBreakdown under a client-side profile. Math
 * mirrors redistributeWeights + scoreToBucket in the server engine.
 */
export function reweight(
  breakdown: ScoreBreakdown,
  profileId: ClientProfileId,
): ReweightResult {
  const weights = PROFILE_WEIGHTS[profileId];
  const entries: Array<{ key: keyof ScoreBreakdown; rawWeight: number; score: number | null }> = (
    Object.keys(weights) as Array<keyof ScoreBreakdown>
  ).map((key) => ({
    key,
    rawWeight: weights[key],
    score: (breakdown[key] as ScoreComponent | undefined)?.score ?? null,
  }));

  const active = entries.filter((e) => e.score !== null);
  if (active.length === 0) return { score: 0, bucket: 'skip' };

  const totalWeight = active.reduce((s, e) => s + e.rawWeight, 0);
  const sum = active.reduce((s, e) => s + (e.score as number) * e.rawWeight, 0);
  const score = Math.round((sum / totalWeight) * 10) / 10;
  return { score, bucket: bucketize(score) };
}

function bucketize(score: number): DealBucket {
  if (score >= DEFAULT_THRESHOLDS.smoking_hot) return 'smoking_hot';
  if (score >= DEFAULT_THRESHOLDS.worth_a_look) return 'worth_a_look';
  if (score >= DEFAULT_THRESHOLDS.fair_value) return 'fair_value';
  return 'skip';
}
